import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logError } from "@/lib/logger";

/**
 * Check if the current user is a superuser (founder/tester with unlimited access).
 * Superusers can:
 * - Quick-apply to ALL jobs regardless of platform
 * - See unlimited matches
 * - Bypass all free-tier limits
 */
export function useSuperuser() {
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkSuperuser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          setIsSuperuser(false);
          return;
        }

        // Server-side check (prevents tampering)
        const { data, error } = await supabase.rpc("check_superuser", {
          p_user_id: session.user.id,
        });

        if (error) {
          logError("Superuser RPC check failed", error);
          setIsSuperuser(false);
          return;
        }

        setIsSuperuser(Boolean(data));
      } catch (error) {
        logError("Superuser check failed", error);
        setIsSuperuser(false);
      } finally {
        setLoading(false);
      }
    };

    checkSuperuser();
    
    // Re-check on auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkSuperuser();
    });

    return () => subscription.unsubscribe();
  }, []);

  return { isSuperuser, loading };
}
